import React, { useContext, useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, CheckCircle2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import Logo from "./Logo";
import { SettingsContext } from "./SettingsContext";
import { submitInquiry } from "../../lib/lnlApi";

const QUICK_REPLIES = [
    { key: "Program information", prompt: "I'd like to know more about your programs." },
    { key: "Tuition and fees", prompt: "How much is tuition?" },
    { key: "Scholarship", prompt: "Are scholarships available?" },
    { key: "Admission requirements", prompt: "What are the admission requirements?" },
    { key: "Transferee evaluation", prompt: "I want to transfer to LNL." },
    { key: "Campus visit", prompt: "I'd like to visit the campus." },
    { key: "Foreign-student inquiry", prompt: "I am a foreign applicant." },
];

const APPLICANT_TYPES = [
    ["kindergarten", "Kindergarten"], ["elementary", "Elementary"], ["junior-high", "Junior High"],
    ["senior-high", "Senior High"], ["college-freshman", "College Freshman"],
    ["college-transferee", "Transferee"], ["als-pept", "ALS / PEPT"], ["foreign", "Foreign"],
];

function BotBubble({ children }) {
    return (
        <div className="flex items-end gap-2">
            <div className="w-7 h-7 rounded-full bg-lnl-green-bg grid place-items-center shrink-0 border border-lnl-green/20">
                <Sparkles className="h-3.5 w-3.5 text-lnl-green-deep" />
            </div>
            <div className="max-w-[80%] rounded-2xl rounded-bl-sm bg-lnl-green-bg text-lnl-charcoal border border-lnl-green/15 px-3 py-2 text-sm leading-relaxed">
                {children}
            </div>
        </div>
    );
}

function UserBubble({ children }) {
    return (
        <div className="flex justify-end">
            <div className="max-w-[80%] rounded-2xl rounded-br-sm bg-lnl-green text-white px-3 py-2 text-sm leading-relaxed">
                {children}
            </div>
        </div>
    );
}

export default function ChatWidget() {
    const settings = useContext(SettingsContext);
    const [open, setOpen] = useState(false);
    const [messages, setMessages] = useState([
        { from: "bot", body: `Hi! I'm the LNL Admissions Assistant. What would you like to ask about?` },
    ]);
    const [concern, setConcern] = useState(null);
    const [phase, setPhase] = useState("pick"); // pick -> details -> done
    const [form, setForm] = useState({ full_name: "", mobile: "", email: "", applicant_type: "college-freshman", message: "", consent: false });
    const [ref, setRef] = useState(null);
    const [busy, setBusy] = useState(false);
    const [unread, setUnread] = useState(1);
    const scrollRef = useRef(null);

    useEffect(() => {
        if (open) setUnread(0);
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, [messages, open]);

    function pushBot(body) { setMessages((m) => [...m, { from: "bot", body }]); if (!open) setUnread((u) => u + 1); }
    function pushUser(body) { setMessages((m) => [...m, { from: "user", body }]); }

    function pickConcern(qr) {
        setConcern(qr.key);
        pushUser(qr.prompt);
        // Foreign applicants get routed to the foreign inquiry page
        if (qr.key === "Foreign-student inquiry") {
            setTimeout(() => pushBot("Foreign-student inquiries are handled by our Foreign Affairs Office. Please continue on the Foreign Student Inquiry page — I'll open it for you."), 300);
            setTimeout(() => { window.location.href = "/admissions/foreign-inquiry"; }, 1400);
            return;
        }
        if (qr.key === "Campus visit") {
            setTimeout(() => pushBot("Great! Campus visits are booked through our short visit form so we can confirm your preferred date. Opening it now."), 300);
            setTimeout(() => { window.location.href = "/campus-visit"; }, 1400);
            return;
        }
        setTimeout(() => {
            pushBot("Perfect. Please share your details below and any specifics you'd like an admissions officer to address. You'll get a reference number after sending.");
            setPhase("details");
        }, 350);
    }

    async function send() {
        if (!form.full_name || !form.mobile || !form.email) return toast.error("Please share your name, mobile and email.");
        if (!form.consent) return toast.error("Please agree to be contacted so Admissions can reply.");
        setBusy(true);
        try {
            const r = await submitInquiry({
                full_name: form.full_name, mobile: form.mobile, email: form.email,
                applicant_type: form.applicant_type,
                main_concern: concern || "Program information",
                preferred_program: null,
                preferred_contact_method: "phone",
                preferred_contact_time: null,
                municipality: null,
                consent: form.consent,
                landing_page: window.location.pathname,
                referral_url: document.referrer,
            });
            setRef(r.reference);
            pushUser(form.message || `${concern} inquiry`);
            setTimeout(() => pushBot(`Thanks, ${form.full_name.split(" ")[0]}! Your inquiry reference is ${r.reference}. An admissions officer will reach you at ${form.mobile} or ${form.email}.`), 300);
            setPhase("done");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not send inquiry. Please try again.");
        } finally { setBusy(false); }
    }

    function restart() {
        setConcern(null); setPhase("pick"); setRef(null);
        setForm({ full_name: "", mobile: "", email: "", applicant_type: "college-freshman", message: "", consent: false });
        setMessages([{ from: "bot", body: "Anything else I can help with? Pick a topic below." }]);
    }

    return (
        <>
            <button
                onClick={() => setOpen((o) => !o)}
                className={`inline-flex items-center gap-2 rounded-full pl-3 pr-4 py-2.5 font-heading text-sm font-semibold shadow-md hover:shadow-lg transition-shadow ${open ? "bg-white border border-lnl-border text-lnl-charcoal" : "bg-white border border-lnl-green text-lnl-green-deep hover:bg-lnl-green-bg"}`}
                data-testid="floating-ask-admissions"
                aria-expanded={open}
                aria-controls="lnl-chat-panel"
            >
                <span className="relative w-8 h-8 rounded-full bg-lnl-green-bg grid place-items-center">
                    {open ? <X className="h-4 w-4 text-lnl-green-deep" /> : <MessageCircle className="h-4 w-4 text-lnl-green-deep" />}
                    {!open && unread > 0 && (
                        <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 grid place-items-center rounded-full bg-lnl-red text-white text-[10px] font-bold" data-testid="chat-unread-dot">{unread}</span>
                    )}
                </span>
                {open ? "Close" : "Ask Admissions"}
            </button>

            {open && (
                <div
                    id="lnl-chat-panel"
                    role="dialog"
                    aria-label="LNL Admissions chat"
                    className="mt-2 w-[calc(100vw-2rem)] sm:w-[380px] max-w-[380px] h-[520px] max-h-[75vh] rounded-xl border border-lnl-border bg-white shadow-2xl flex flex-col overflow-hidden"
                    data-testid="chat-window"
                >
                    <div className="flex items-center gap-3 px-4 py-3 bg-lnl-green-deep text-white">
                        <Logo size={30} />
                        <div className="flex-1">
                            <div className="font-heading font-bold text-sm leading-tight">LNL Admissions</div>
                            <div className="text-[11px] opacity-90 flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-lnl-gold-soft animate-pulse" /> Usually replies within one working day</div>
                        </div>
                        <button onClick={() => setOpen(false)} aria-label="Close chat" className="p-1 rounded-md hover:bg-white/10" data-testid="chat-close">
                            <X className="h-4 w-4" />
                        </button>
                    </div>

                    <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-3 space-y-2 bg-lnl-off-white" data-testid="chat-messages">
                        {messages.map((m, i) => m.from === "bot" ? <BotBubble key={i}>{m.body}</BotBubble> : <UserBubble key={i}>{m.body}</UserBubble>)}

                        {phase === "pick" && (
                            <div className="pt-1 flex flex-wrap gap-1.5">
                                {QUICK_REPLIES.map((qr) => (
                                    <button
                                        key={qr.key}
                                        onClick={() => pickConcern(qr)}
                                        className="text-xs font-heading font-semibold text-lnl-green-deep bg-white border border-lnl-green/40 hover:bg-lnl-green-bg rounded-full px-3 py-1.5 transition-colors"
                                        data-testid={`chat-quickreply-${qr.key.replace(/[^a-z]+/gi, "-").toLowerCase()}`}
                                    >
                                        {qr.key}
                                    </button>
                                ))}
                            </div>
                        )}

                        {phase === "done" && ref && (
                            <div className="pt-2">
                                <div className="lnl-card p-3 flex items-start gap-2 bg-white">
                                    <CheckCircle2 className="h-4 w-4 text-lnl-green mt-0.5" />
                                    <div>
                                        <div className="font-heading font-semibold text-xs text-lnl-charcoal">Reference {ref}</div>
                                        <div className="text-[11px] text-lnl-gray">Please save this number for follow-up.</div>
                                    </div>
                                </div>
                                <button onClick={restart} className="mt-2 text-xs font-heading font-semibold text-lnl-green-deep underline underline-offset-2" data-testid="chat-restart">Ask another question</button>
                            </div>
                        )}
                    </div>

                    {phase === "details" && (
                        <div className="border-t border-lnl-border bg-white p-3 space-y-2" data-testid="chat-details-form">
                            <div className="grid grid-cols-2 gap-2">
                                <input aria-label="Your name" placeholder="Your name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="col-span-2 rounded-md border border-lnl-border px-2.5 py-2 text-sm" data-testid="chat-name" />
                                <input aria-label="Mobile" placeholder="Mobile" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} className="rounded-md border border-lnl-border px-2.5 py-2 text-sm" data-testid="chat-mobile" />
                                <input aria-label="Email" type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-md border border-lnl-border px-2.5 py-2 text-sm" data-testid="chat-email" />
                                <select aria-label="Applicant type" value={form.applicant_type} onChange={(e) => setForm({ ...form, applicant_type: e.target.value })} className="col-span-2 rounded-md border border-lnl-border px-2.5 py-2 text-sm bg-white" data-testid="chat-applicant-type">
                                    {APPLICANT_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                                </select>
                                <textarea aria-label="Message" placeholder="Add any specifics for Admissions (optional)" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={2} className="col-span-2 rounded-md border border-lnl-border px-2.5 py-2 text-sm" data-testid="chat-message" />
                            </div>
                            <label className="flex items-start gap-2 text-[11px] text-lnl-gray">
                                <input type="checkbox" checked={form.consent} onChange={(e) => setForm({ ...form, consent: e.target.checked })} className="mt-0.5" data-testid="chat-consent" />
                                I agree to be contacted by LNL Admissions.
                            </label>
                            <button onClick={send} disabled={busy} className="lnl-btn-primary w-full justify-center py-2" data-testid="chat-send">
                                <Send className="h-3.5 w-3.5" /> {busy ? "Sending…" : "Send inquiry"}
                            </button>
                        </div>
                    )}

                    <div className="border-t border-lnl-border px-3 py-2 text-[10.5px] text-lnl-gray flex items-center justify-between bg-white">
                        <span>Also reach us at <a className="lnl-link" href={`tel:${(settings?.mobile || "").replace(/[^0-9+]/g, "")}`}>{settings?.mobile}</a></span>
                        <a className="lnl-link" href="/contact">Full contact form</a>
                    </div>
                </div>
            )}
        </>
    );
}
